import { AppConstants } from '@app/app.constants';
import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { Place } from './place';
import { Waiter } from './waiter';
import { Session } from './session';
import { Address } from './address';
import { UserAddress } from './useraddress';

export interface _User {
    status: string;
    email: string;
    password?: string;
    firstname: string;
    lastname: string;
    fullname?: string;
    photo: {
        url: string;
        b64: any;
    },
    phone: string;
    lang: string;
    license: boolean;
    pin?: string;           // PIN NUEVO en claro (SOLO escritura: al ponerlo). El
                            // server lo hashea; nunca vuelve al cliente.
    oldpin?: string;        // PIN ACTUAL en claro (SOLO escritura): lo exige el server
                            // para cambiar el PIN propio. No es columna; se consume.
    has_pin?: boolean;      // ¿tiene PIN de owner configurado? (lo computa el server).
    rfid?: string;          // PULSERA RFID nueva (SOLO escritura: al asignarla).
    clearrfid?: boolean;    // QUITAR la pulsera (SOLO escritura, transitorio).
    rfidreassign?: boolean; // la pulsera nueva se la QUITA a quien la tenga (SOLO escritura, transitorio).
    has_rfid?: boolean;     // ¿tiene pulsera asignada? (lo computa el server).
};

interface _UserData extends _User {
    objid?: number;
    _uuid?: string;
    created?: Date;
};

abstract class UserData extends DataObject {
    protected _user: _UserData = {
        status: null,
        email: null,
        firstname: null,
        lastname: null,
        fullname: null,
        photo: {
            url: null,
            b64: null
        },
        phone: null,
        lang: null,
        license: true,
        pin: null,
        oldpin: null,
        has_pin: null,
        rfid: null,
        clearrfid: null,
        rfidreassign: null,
        has_rfid: null
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);
        this._user.created = new Date();
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(){
        return this._user.created;
    }

    get status(): string{
        return this._user.status;
    }

    set status(value: string){
        if (this.patchValue(this._user, 'status', value)){
            this.ToUpdate = true;
        }
    }

    get email(): string{
        return this._user.email;
    }

    set email(value: string){
        // Normalizar a minúsculas (y sin espacios) SIEMPRE: el correo es
        // case-insensitive y el servidor lo deduplica/almacena en minúsculas.
        // Cubre registro y alta de camareros (el user del STAFF cuelga de aquí).
        let _norm = value ? value.trim().toLowerCase() : value;
        if (this.patchValue(this._user, 'email', _norm)){
            this.ToUpdate = true;
        }
    }

    get firstname(): string{
        return this._user.firstname;
    }

    set firstname(value: string){
        if (this.patchValue(this._user, 'firstname', value)){
            this.ToUpdate = true;
        }
    }

    get lastname(): string{
        return this._user.lastname;
    }

    set lastname(value: string){
        if (this.patchValue(this._user, 'lastname', value)){
            this.ToUpdate = true;
        }
    }

    get fullname(): string {
        return this._user.fullname;
    }

    get photo(): string{
        return this._user.photo.url;
    }

    set photo(value: string){
        if (this.patchValue(this._user.photo, 'url', value)){
            this.ToUpdate = true;
        }
    }
   
    get base64(): any {
        return this._user.photo.b64;
    }

    set base64(value: any){
        if (this.patchValue(this._user.photo, 'b64', value)){
            this.ToUpdate = true;
        }
    }

    get phone(): string{
        return this._user.phone;
    }

    set phone(value: string){
        if (this.patchValue(this._user, 'phone', value)){
            this.ToUpdate = true;
        }
    }

    get lang(): string{
        return this._user.lang;
    }

    set lang(value: string){
        if (this.patchValue(this._user, 'lang', value)){
            this.ToUpdate = true;
        }
    }

    get license(): boolean{
        return this._user.license;
    }

    set license(value: boolean){
        if (this.patchValue(this._user, 'license', value)){
            this.ToUpdate = true;
        }
    }

    // PIN del OWNER para el bloqueo por camarero. NUEVO en claro (solo escritura:
    // al ponerlo). El server lo hashea y la unicidad se valida server-side. Tras
    // cargar es null (el server nunca devuelve el PIN/hash): has_pin indica si hay
    // uno configurado.
    get pin(): string{
        return this._user.pin;
    }

    set pin(value: string){
        let _norm = (value != null && ('' + value).trim() != '') ? ('' + value).trim() : null;
        if (this.patchValue(this._user, 'pin', _norm)){
            this.ToUpdate = true;
        }
    }

    // PIN ACTUAL (write-only): se envía junto al nuevo para que el server verifique
    // que quien cambia el PIN propio conoce el actual. No se carga ni se muestra.
    set oldpin(value: string){
        let _norm = (value != null && ('' + value).trim() != '') ? ('' + value).trim() : null;
        this.patchValue(this._user, 'oldpin', _norm);
    }

    get has_pin(): boolean {
        return !!(this._user.has_pin || this._user.pin);
    }

    // PULSERA RFID del owner (solo escritura al asignarla; el server nunca la
    // devuelve: has_rfid indica si hay una).
    get rfid(): string {
        return this._user.rfid;
    }

    set rfid(value: string){
        let _norm = (value != null && ('' + value).trim() != '') ? ('' + value).trim() : null;
        if (this.patchValue(this._user, 'rfid', _norm)){
            if (_norm){
                this._user.clearrfid = null;
            }
            this.ToUpdate = true;
        }
    }

    // REASIGNAR: la pulsera nueva ya la tiene un camarero (rfidcheck.php) y el dueño
    // confirmó quitársela. Transitorio; el server lo hace en la misma transacción.
    set rfidreassign(value: boolean){
        if (this.patchValue(this._user, 'rfidreassign', value ? true : null)){
            this.ToUpdate = true;
        }
    }

    set clearrfid(value: boolean){
        if (this.patchValue(this._user, 'clearrfid', value ? true : null)){
            if (value){
                this._user.rfid = null;
            }
            this.ToUpdate = true;
        }
    }

    get has_rfid(): boolean {
        if (this._user.clearrfid){
            return false;
        }
        return !!(this._user.has_rfid || this._user.rfid);
    }

    // Igual que en Waiter: el eco del server puede ser idéntico al anterior y base.OnUpdate
    // no llamar a _OnUpdate; los transitorios (pin/oldpin/rfid/clearrfid/rfidreassign) se
    // limpian aquí en cualquier caso cuando no queda cambio local pendiente.
    protected OnUpdate(info): boolean {
        let _changed = super.OnUpdate(info);
        this._ClearCredentialTransients();
        return _changed;
    }

    private _ClearCredentialTransients(){
        if (this._user.pin && !this.ToUpdate && !this.ToInsert){
            this._user.pin = null;
            this._user.oldpin = null;
        }
        if ((this._user.rfid || this._user.clearrfid || this._user.rfidreassign) && !this.ToUpdate && !this.ToInsert){
            this._user.rfid = null;
            this._user.clearrfid = null;
            this._user.rfidreassign = null;
        }
    }

    /****************************/
    /* CHILDREN MANAGEMENT      */
    /****************************/

    AddPlace(child: Place){
        this.AddChild('places', child, 'user');
    }

    DelPlace(child: Place){
        this.DelChild('places', child, 'user');
    }

    AddWaiter(child: Waiter){
        this.AddChild('waiter', child, 'user');
    }

    AddSession(child: Session){
        this.AddChild('session', child, 'user');
    }

    AddAddress(child: UserAddress){
        this.AddChild('address', child, 'user');
    }

    DelAddress(child: UserAddress){
        this.DelChild('address', child, 'user');
    }

    /****************************/
    /* CHILD ACCESS             */
    /****************************/

    get places() : Array <Place> {
        return this._chldlist['places'] || [];
    }

    get sessions() : Array <Session> {
        return this._chldlist['session'] || [];
    }

    get waiters() : Array <Waiter> {
        return this._chldlist['waiter'] || [];
    }

    get address(): Array <UserAddress> {
        return this._chldlist['address'] || [];
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return (async () => {
            let _base64 = this.base64;
            if (!_base64){
                let _uploadUrl = AppConstants.baseURL + AppConstants.uploadPath;
                if (this.photo && !this.photo.startsWith(_uploadUrl)){
                    this.base64 = await this.uploadTobase64(this.photo);
                }
            }
    
            return {
                email: this._user.email,
                firstname: this._user.firstname,
                lastname: this._user.lastname,
                photo: {
                    url: this.uploadToMysql(this.photo),
                    b64: this.base64
                },
                phone: this._user.phone,
                lang: this._user.lang,
                license: this._user.license ? '1': '0',
                pin: this._user.pin,
                oldpin: this._user.oldpin,
                rfid: this._user.rfid,
                clearrfid: this._user.clearrfid,
                rfidreassign: this._user.rfidreassign
            };
        })();
    }

    protected get Depend() {
        return { /* empty */ };
    }

    protected get Children(){
        let _children = [];

        for(let _item of this.places){
            _children.push(_item)
        }

        for(let _item of this.sessions){
            _children.push(_item)
        }

        for(let _item of this.waiters){
            _children.push(_item)
        }

        return _children;
    }

    /****************************/
    /* DATA OBJECT              */
    /****************************/

    private _patchData(_user: _User){
        _user.fullname = this._fullname(_user);

        let _toUpdate = false;
        
        _toUpdate = this.patchValue(this._user, 'status', _user['status']) || _toUpdate;
        _toUpdate = this.patchValue(this._user, 'email', _user['email']) || _toUpdate;
        _toUpdate = this.patchValue(this._user, 'firstname', _user['firstname']) || _toUpdate;
        _toUpdate = this.patchValue(this._user, 'lastname', _user['lastname']) || _toUpdate;
        _toUpdate = this.patchValue(this._user, 'fullname', _user['fullname']) || _toUpdate;
        _toUpdate = this.patchValue(this._user, 'photo', _user['photo']) || _toUpdate;
        _toUpdate = this.patchValue(this._user, 'phone', _user['phone']) || _toUpdate;
        _toUpdate = this.patchValue(this._user, 'lang', _user['lang']) || _toUpdate;
        _toUpdate = this.patchValue(this._user, 'license', _user['license']) || _toUpdate;
        // OJO: NO parchear 'pin' desde datos cargados — el server nunca lo envía
        // (solo has_pin) y parchearlo pisaría un PIN nuevo pendiente de enviar.
        _toUpdate = this.patchValue(this._user, 'has_pin', _user['has_pin']) || _toUpdate;
        _toUpdate = this.patchValue(this._user, 'has_rfid', _user['has_rfid']) || _toUpdate;

        return _toUpdate;
    }

    set Data(_user: _User){
        this.patchValue(this._user, 'created', _user['created']);
        
        if (this._patchData(_user)){
            this.ToUpdate = true;
        }
    }

    get Info(){
        return this._user;
    }

    set Info(value){
        this.DoPatchValues(value);
    }
   
    private _fullname(_user){
        return _user.firstname ? _user.firstname + (_user.lastname ? (' ' + _user.lastname) : '') : null;
    }

    private DoPatchValues(_user: _User){
        this._patchData(_user);
    }

    private _ddbb(info): _UserData {
        let _user: _UserData = {
            objid: info['objid'],
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            status: info['status'],
            email: info['email'],
            firstname: info['firstname'],
            lastname: info['lastname'],
            photo: {
                url: this.mysqlToUpload(info['photo']),
                b64: null
            },             
            phone: info['phone'],
            lang: info['lang'],
            license: (info['license'] == '1'),
            pin: null,                      // el server nunca envía el PIN/hash
            has_pin: !!info['has_pin'],
            rfid: null,                     // el server nunca envía el tag
            clearrfid: null,
            rfidreassign: null,
            has_rfid: !!info['has_rfid']
        };

        return _user;
    }

    protected _OnUpdate(info){
        let _user = this._ddbb(info);
        this.patchValue(this._user, 'objid', _user['objid']);
        this.patchValue(this._user, 'created', _user['created']);
        this.DoPatchValues(_user);

        // El PIN NUEVO en claro solo vive en memoria hasta que el server confirma
        // la escritura (echo con has_pin). Ya persistido/hasheado: lo borramos para
        // no retenerlo. Se preserva mientras haya un cambio local sin enviar.
        this._ClearCredentialTransients();

        if (info['places']){    // update children: 'places'
            this.SetChildren <Place> (info['places'], 'places', Place, 'user');
        }

        if (info['waiter']){    // update children: 'waiter'
            this.SetChildren <Waiter> (info['waiter'], 'waiter', Waiter, 'user');
        }

        if (info['address']){    // update children: 'address'
            this.SetChildren <UserAddress> (info['address'], 'address', UserAddress, 'user');
        }

        if (info['session']){    // update children: 'session'
            this.SetChildren <Session> (info['session'], 'session', Session, 'user');
        }
    }
}

export class User extends UserData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('USER', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): User {
        return this._Copy(store) as User;
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsActive(){
        return (this._user.status == 'AC');
    }

    get IsPending(){
        return (this._user.status == 'PA');
    }

    // Avatar para la cabecera del bloqueo (owner): su foto o un avatar generado
    // a partir del nombre (cae al email si no hay nombre, para no quedar vacío).
    get avatar(): string {
        return this.photo || this.avatarPhoto(this.fullname || this.email);
    }

    /* delivery address */

    AddDelivery(child: Address){
        let _useraddress = new UserAddress(null, this.data);
        if (_useraddress){
            _useraddress.user = this;
            _useraddress.address = child;
            _useraddress.status = 'AC';
        }

        this.AddAddress(_useraddress);
    }

    DelDelivery(child: Address){
        let _address = this.address;
        for(let _idx = _address.length-1; _idx >= 0; _idx--){
            let _current = _address[_idx];
            if (_current.address == child){
                this.DelAddress(_current);
            }
        }
    }

    get delivery() : Array <Address> {
        let _addresses = [];
        for(let _useraddress of this.address){
            if (_useraddress.IsValid){
                _addresses.push(_useraddress.address);
            }
        }
        return _addresses;
    }
}